<!--
  Sketch component for the PMA name-resolution review queue.

  Layout: left pane lists pending resolutions; right pane shows the
  selected one + candidate companies. Reviewer picks a candidate (or
  "no match") and the row is resolved + linked server-side in one call.
-->
<template>
  <div class="w-full h-[calc(100vh-3.5rem)] flex bg-gray-50 text-sm">
    <!-- Left: queue -->
    <div class="w-[360px] min-w-[360px] border-r border-gray-300 bg-white overflow-y-auto">
      <div class="sticky top-0 bg-white border-b border-gray-300 px-3 py-2 flex justify-between items-center">
        <span class="font-semibold text-gray-700 tracking-wide">PMA Review Queue</span>
        <span class="text-xs text-gray-500">{{ count }} pending</span>
      </div>
      <div
        v-for="row in rows"
        :key="row.id"
        class="px-3 py-2 border-b border-gray-200 cursor-pointer hover:bg-blue-50"
        :class="{ 'bg-blue-100': selected && selected.id === row.id }"
        @click="select(row)"
      >
        <div class="font-medium truncate">{{ row.pma_company_name }}</div>
        <div class="text-xs text-gray-500 truncate">
          {{ row.pma_city || '—' }}, {{ row.pma_state || '—' }}
        </div>
        <div class="text-xs text-gray-400">{{ row.pma_row_count }} PMAs</div>
      </div>
      <div class="px-3 py-3 text-center">
        <button
          v-if="hasNextPage"
          class="text-xs text-blue-600 hover:underline"
          @click="loadNextPage"
        >Load more</button>
      </div>
    </div>

    <!-- Right: detail + candidates -->
    <div class="flex-1 overflow-y-auto">
      <div v-if="!selected" class="h-full flex items-center justify-center text-gray-400">
        Select a row to review.
      </div>

      <div v-else class="p-6 space-y-6 max-w-3xl">
        <div>
          <div class="text-xs uppercase tracking-widest text-gray-500">PMA filing</div>
          <div class="text-lg font-semibold">{{ selected.pma_company_name }}</div>
          <div class="text-sm text-gray-600">
            {{ selected.pma_city || '—' }}, {{ selected.pma_state || '—' }}
            <span class="text-gray-400">·</span>
            <span class="text-gray-500">{{ selected.pma_row_count }} PMA rows ride on this decision</span>
          </div>
          <div v-if="selected.pma_addresses && selected.pma_addresses.length" class="mt-2 text-sm text-gray-700">
            <div class="text-xs uppercase tracking-widest text-gray-500 mb-1">
              Address{{ selected.pma_addresses.length > 1 ? 'es' : '' }}
            </div>
            <div v-for="(addr, i) in selected.pma_addresses" :key="i">
              {{ addr.address || '—' }}<span v-if="addr.zip">, {{ addr.zip }}</span><span v-if="addr.country">, {{ addr.country }}</span>
            </div>
          </div>
        </div>

        <div>
          <div class="text-xs uppercase tracking-widest text-gray-500 mb-2">Candidates</div>
          <div v-if="!selected.candidates.length" class="text-gray-500 italic">
            No candidate companies surfaced by trigram or location match.
          </div>
          <div
            v-for="c in selected.candidates"
            :key="c.company_id"
            class="border border-gray-300 rounded-md p-3 mb-2 hover:border-blue-400 cursor-pointer"
            :class="{ 'border-blue-500 bg-blue-50': pickedId === c.company_id }"
            @click="pickedId = c.company_id"
          >
            <div class="flex justify-between items-start">
              <div>
                <div class="flex items-baseline space-x-2">
                  <button
                    type="button"
                    class="font-medium text-blue-600 hover:underline text-left disabled:opacity-60 disabled:cursor-wait"
                    :disabled="loadingCompanyId !== null"
                    :class="{ 'cursor-wait': loadingCompanyId !== null }"
                    @click.stop="openCompanyModal(c.company_id)"
                  >
                    {{ c.company_name }}
                  </button>
                  <span class="text-xs text-gray-400">id {{ c.company_id }}</span>
                  <svg
                    v-if="loadingCompanyId === c.company_id"
                    class="w-4 h-4 animate-spin text-blue-600"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                  </svg>
                </div>
                <div v-if="c.company_nickname" class="text-xs text-gray-500">
                  aka {{ c.company_nickname }}
                </div>
                <div class="text-sm text-gray-700 mt-1">
                  {{ c.address || '—' }}
                </div>
                <div class="text-xs text-gray-600">
                  {{ c.city || '—' }}, {{ c.state || '—' }}<span v-if="c.zip">, {{ c.zip }}</span> · {{ c.country || '—' }}
                </div>
                <div v-if="c.parent_company" class="text-xs text-gray-500">
                  Parent: {{ c.parent_company }}
                </div>
              </div>
              <div class="text-xs">
                <span class="px-2 py-0.5 rounded bg-gray-200">{{ c.status }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Decision controls -->
        <div class="border-t border-gray-300 pt-4">
          <div class="flex items-center space-x-2 mb-3">
            <span class="text-xs uppercase tracking-widest text-gray-500">Confidence:</span>
            <select v-model="confidence" class="border border-gray-300 rounded-md px-2 py-1 text-sm">
              <option value="high">high</option>
              <option value="medium">medium</option>
              <option value="low">low (hold)</option>
              <option value="none">no match</option>
            </select>
          </div>
          <div class="flex space-x-2">
            <button
              class="px-4 py-2 rounded-md bg-blue-600 text-white text-sm disabled:opacity-50"
              :disabled="!canConfirm"
              @click="confirm"
            >
              {{ confidence === 'none' ? 'Mark as no match' : `Link as ${confidence}` }}
            </button>
            <button
              class="px-4 py-2 rounded-md border border-gray-300 text-sm text-gray-700"
              @click="skip"
            >
              Skip
            </button>
          </div>
        </div>
      </div>
    </div>

    <base-modal :is-form-visible="companyModalVisible" @close="closeCompanyModal">
      <template #header>
        <h2 class="text-lg">{{ fullCompany ? fullCompany.company_name : '' }} {{ fullCompany ? fullCompany.id : '' }}</h2>
      </template>
      <template #body>
        <EditCompany
          v-if="fullCompany"
          :company="fullCompany"
          :campaigns="[]"
          @close="closeCompanyModal"
        />
      </template>
    </base-modal>
  </div>
</template>


<script setup>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { useToast } from "vue-toast-notification";

import { getPendingResolutions, resolvePMA } from "@/services/PMAResolution";
import { getCompanyByID } from "@/services/Company";
import EditCompany from "@/components/EditCompany.vue";

const store = useStore();
const $toast = useToast();

const token = computed(() => store.state.auth.token);

const rows = ref([]);
const count = ref(0);
const page = ref(1);
const hasNextPage = ref(false);

const selected = ref(null);
const pickedId = ref(null);
const confidence = ref("high");

const companyModalVisible = ref(false);
const fullCompany = ref(null);
const loadingCompanyId = ref(null);


async function loadPage(targetPage) {
  try {
    const data = await getPendingResolutions({
      token: token.value,
      page: targetPage,
      status: "pending",
    });
    if (targetPage === 1) rows.value = data.results;
    else rows.value = rows.value.concat(data.results);
    count.value = data.count;
    page.value = targetPage;
    hasNextPage.value = !!data.next;
    if (!selected.value && rows.value.length) select(rows.value[0]);
  } catch (e) {
    $toast.error("Failed to load review queue");
  }
}

function loadNextPage() {
  loadPage(page.value + 1);
}

function select(row) {
  selected.value = row;
  pickedId.value = null;
  confidence.value = "high";
}

const canConfirm = computed(() => {
  if (confidence.value === "none") return true;
  return pickedId.value !== null;
});

async function confirm() {
  if (!selected.value) return;
  try {
    const result = await resolvePMA({
      token: token.value,
      id: selected.value.id,
      company_id: confidence.value === "none" ? null : pickedId.value,
      confidence: confidence.value,
    });
    $toast.success(`Linked ${result.pmas_linked} PMA rows`);
    // Drop the resolved row from the queue and advance.
    const idx = rows.value.findIndex(r => r.id === selected.value.id);
    rows.value.splice(idx, 1);
    count.value = Math.max(0, count.value - 1);
    select(rows.value[idx] || rows.value[idx - 1] || null);
  } catch (e) {
    $toast.error("Failed to record decision");
  }
}

function skip() {
  if (!selected.value) return;
  const idx = rows.value.findIndex(r => r.id === selected.value.id);
  select(rows.value[idx + 1] || null);
}

async function openCompanyModal(companyId) {
  if (loadingCompanyId.value !== null) return;
  loadingCompanyId.value = companyId;
  try {
    const result = await getCompanyByID({ token: token.value, companyId });
    if (result.response.status === 200) {
      fullCompany.value = result.response.data;
      companyModalVisible.value = true;
    } else {
      $toast.error("Could not fetch company");
    }
  } catch (e) {
    $toast.error("Could not fetch company");
  } finally {
    loadingCompanyId.value = null;
  }
}

function closeCompanyModal() {
  companyModalVisible.value = false;
  fullCompany.value = null;
}


onMounted(() => loadPage(1));
</script>
